import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import { Video, Mic, FileText, Calendar, Clock, X } from 'lucide-react';
import { parseEAT } from '../utils/eatTime';

// ============================================================
// Global Class-Start Alarm (Uber-like ding-dong pop-up)
// Fires at 5 min and 2 min before any enrolled/confirmed session
// (Live Class, 1-on-1, Audio Room, Exam) for students AND teachers.
// Uses Web Audio API to synthesize a two-tone ding-dong chime that
// repeats every 1.5s until the user dismisses or joins.
// ============================================================
export default function ClassStartAlarm() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeAlarm, setActiveAlarm] = useState(null); // { session, minutesLeft, link }
  const alertedRef = useRef(new Set()); // keys like `${id}-5min`, `${id}-2min`
  const audioCtxRef = useRef(null);
  const chimeTimerRef = useRef(null);

  // ---------- Ding-Dong chime synthesis ----------
  const playChime = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      // "Ding" — high E5 (659 Hz)
      const playTone = (freq, startAt, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + startAt);
        // Bell-like exp decay
        gain.gain.setValueAtTime(0.0001, now + startAt);
        gain.gain.exponentialRampToValueAtTime(0.45, now + startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + startAt + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + startAt);
        osc.stop(now + startAt + duration + 0.05);
      };
      playTone(880, 0, 0.55);      // A5 (ding)
      playTone(587.33, 0.28, 0.75); // D5 (dong)
    } catch (e) { /* noop */ }
  }, []);

  const startChimeLoop = useCallback(() => {
    playChime();
    chimeTimerRef.current = setInterval(playChime, 1600);
  }, [playChime]);

  const stopChimeLoop = useCallback(() => {
    if (chimeTimerRef.current) { clearInterval(chimeTimerRef.current); chimeTimerRef.current = null; }
  }, []);

  // ---------- Poll upcoming sessions ----------
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const getLinkFor = (s) => {
      const map = {
        booking: `/session/${s.id}`,
        live_class: `/live-classes/${s.id}`,
        audio_room: `/audio-rooms/${s.id}`,
        exam: `/exams/${s.id}`,
      };
      return map[s.session_type] || '/';
    };

    const scanSessions = async () => {
      try {
        // Teachers see their own sessions; students see their enrolled ones
        const endpoint = user.role === 'teacher' || user.role === 'admin'
          ? `${API}/teacher/dashboard/upcoming`
          : `${API}/availability/my-upcoming-all`;
        const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const sessions = await res.json();
        if (!Array.isArray(sessions)) return;

        const now = new Date();
        for (const s of sessions) {
          if (!s.date || !s.start_time) continue;
          const scheduled = parseEAT(s.date, s.start_time);
          if (!scheduled) continue;
          const diffMin = (scheduled - now) / 60000;

          // 6-min window: [5.5, 6.0)
          if (diffMin >= 5.5 && diffMin < 6.0) {
            const key = `${s.id}-6min`;
            if (!alertedRef.current.has(key)) {
              alertedRef.current.add(key);
              setActiveAlarm({ session: s, minutesLeft: 6, link: getLinkFor(s) });
              return; // show one alarm at a time
            }
          }
          // 3-min window: [2.5, 3.0)
          if (diffMin >= 2.5 && diffMin < 3.0) {
            const key = `${s.id}-3min`;
            if (!alertedRef.current.has(key)) {
              alertedRef.current.add(key);
              setActiveAlarm({ session: s, minutesLeft: 3, link: getLinkFor(s) });
              return;
            }
          }
        }
      } catch { /* noop */ }
    };

    scanSessions();
    const iv = setInterval(scanSessions, 30000); // poll every 30s
    return () => clearInterval(iv);
  }, [user]);

  // Start chime when alarm becomes active, stop when dismissed
  useEffect(() => {
    if (activeAlarm) startChimeLoop();
    else stopChimeLoop();
    return stopChimeLoop;
  }, [activeAlarm, startChimeLoop, stopChimeLoop]);

  // Cleanup audio context on unmount
  useEffect(() => () => {
    stopChimeLoop();
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} }
  }, [stopChimeLoop]);

  if (!activeAlarm) return null;

  const { session, minutesLeft, link } = activeAlarm;
  const typeMeta = {
    booking: { Icon: Video, label: '1-on-1 Session', color: 'from-purple-500 to-pink-500' },
    live_class: { Icon: Video, label: 'Live Class', color: 'from-red-500 to-orange-500' },
    audio_room: { Icon: Mic, label: 'Audio Room', color: 'from-cyan-500 to-blue-500' },
    exam: { Icon: FileText, label: 'Exam', color: 'from-amber-500 to-yellow-500' },
  };
  const meta = typeMeta[session.session_type] || typeMeta.booking;
  const Icon = meta.Icon;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="class-start-alarm"
    >
      <div className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl shadow-black/60 bg-white">
        {/* Gradient header with pulsing icon */}
        <div className={`bg-gradient-to-br ${meta.color} p-6 text-center relative overflow-hidden`}>
          <div className="absolute inset-0 bg-white/5 animate-pulse" />
          <div className="relative">
            <div className="w-20 h-20 mx-auto rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center mb-3 ring-4 ring-white/30 animate-[ping_1.5s_ease-in-out_infinite]" style={{ animationDuration: '1.2s' }}>
              <Icon className="w-10 h-10 text-white drop-shadow-lg" />
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
              <Icon className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="relative mt-4 text-2xl font-black text-white tracking-tight">
            Starting in {minutesLeft} min
          </h2>
          <p className="relative text-white/90 text-sm mt-1 font-medium">{meta.label}</p>
        </div>

        {/* Details */}
        <div className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-3 text-center">
            {session.title || session.topic || session.subject || 'Session'}
          </h3>
          <div className="flex items-center justify-center gap-4 text-sm text-slate-600 mb-6">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{session.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{session.start_time}</span>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setActiveAlarm(null)}
              className="flex-1 px-4 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
              data-testid="alarm-dismiss-btn"
            >
              <X className="w-4 h-4" /> Dismiss
            </button>
            <button
              onClick={() => { setActiveAlarm(null); navigate(link); }}
              className={`flex-1 px-4 py-3 rounded-xl bg-gradient-to-r ${meta.color} text-white font-bold shadow-lg hover:scale-[1.02] transition-transform`}
              data-testid="alarm-join-btn"
            >
              {session.session_type === 'exam' ? 'View' : 'Join Now'}
            </button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-3">
            You'll be alerted again {minutesLeft === 6 ? '3 minutes' : 'when it starts'} before it begins.
          </p>
        </div>
      </div>
    </div>
  );
}
